import { CreativeReportData, Status, Platform } from "@/screens/creatives/types";
import CreativeTable from "../../table/CreativeTable";
import OverviewSectionWrapper from "../section/OverviewSectionWrapper";
import { IconPokerChip } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useGlobalStore } from "@/GlobalStore";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import ErrorState from "@/components/common/errorStates/ErrorState";
import NothingToShow from "@/components/common/emptyStates/NothingToShow";
import { flexRender } from "@tanstack/react-table";
import { getMetricFormulaLabel, getPlatformIcon } from "@/screens/utils/Helpers";
import { ColumnOption, CreativeReportMetadata } from "@/types";
import EditMetricsToggle from "@/components/common/metrics/components/EditMetricsToggle";
import Metrics from "@/components/common/metrics";

interface Props {
  reportData: CreativeReportData;
  title: string;
  status: Status;
  loading: boolean;
  error?: { hasError: boolean; message?: string };
  onRetry?: () => void;
  onMetricChange?: (metrics: string[]) => void;
  reportMetadata?: CreativeReportMetadata;
}

const AdAccountOverview = ({
  reportData,
  title,
  error,
  loading,
  onRetry,
  onMetricChange,
  reportMetadata,
}: Props) => {
  const dataConfigurations = useGlobalStore((s) => s.dataConfigurations);
  const metricsOptions = useMemo(
    () =>
      dataConfigurations?.column_choices.find((c) => c.key === "metric")?.options ?? [],
    [dataConfigurations],
  );
  const selectedAccount = useGlobalStore((s) => s.selectedAccount);
  const [showAddMetric, setShowAddMetric] = useState<boolean>(false);

  const selectedMetricsIds =
    reportData?.section?.configuration?.column_choices?.["metric"] ?? [];
  const selectedMetrics =
    (selectedMetricsIds
      .map((metricId) => metricsOptions?.find((m) => m.key === metricId))
      .filter(Boolean) as typeof metricsOptions) ?? [];

  const handleMetricChange = (metrics: ColumnOption[]) => {
    onMetricChange?.(metrics.map((m) => m.key));
  };

  // Make a map of metrics options for easy lookup by key
  const metricsOptionsMap = useMemo(() => {
    return (metricsOptions || []).reduce<{ [key: string]: ColumnOption }>(
      (acc, option) => {
        acc[option.key] = option;
        return acc;
      },
      {},
    );
  }, [metricsOptions]);

  const dataColumns = useMemo(
    () =>
      reportData?.data?.columns
        ?.filter((c) => c.hidden_from_table !== true)
        ?.map((c) => {
          const metricOption = metricsOptionsMap[c.key];
          if (!metricOption) return c;
          const metricFormula = metricOption.metric_formula ?? null;
          return {
            ...c,
            display_formula: getMetricFormulaLabel(metricFormula, metricsOptions),
          };
        }) ?? [],
    [reportData?.data?.columns, metricsOptionsMap, metricsOptions],
  );

  return (
    <OverviewSectionWrapper
      title={title}
      titleIcon={IconPokerChip}
      rightSideHeader={
        !error?.hasError &&
        ((loading && reportData?.status === "partial") || !loading) ? (
          <EditMetricsToggle
            active={showAddMetric}
            onToggle={() => setShowAddMetric((i) => !i)}
          />
        ) : undefined
      }
    >
      {loading && reportData?.status !== "partial" ? (
        <div className="w-full h-[300px] flex items-center justify-center">
          <WaveLoader />
        </div>
      ) : error?.hasError ? (
        <div className="w-full h-[300px] flex items-center justify-center">
          <ErrorState onRetry={onRetry} />
        </div>
      ) : !reportData || !reportData.data ? (
        <div className="w-full h-[300px] flex items-center justify-center">
          <NothingToShow />
        </div>
      ) : (
        <div>
          {showAddMetric && (
            <Metrics
              selectedMetrics={selectedMetrics}
              metricsOptions={metricsOptions}
              onMetricChange={handleMetricChange}
              className="mb-2"
            />
          )}

          <CreativeTable
            reportMetadata={reportMetadata}
            data={reportData?.data?.rows}
            columns={dataColumns}
            totalRow={
              reportData?.data?.rows.length > 1 ? reportData?.data?.total : undefined
            }
            partialColumns={reportData?.data?.partial_columns}
            status={reportData.status}
            showPaginationIfPagination={true}
            paginationTitle="Accounts per page"
            metricOptions={metricsOptions}
            selectedMetrics={selectedMetrics}
            onMetricChange={handleMetricChange}
            showAddMetricButton={true}
            overwritePrimaryCell={(cell) => {
              // Get the raw cell value
              const cellValue = cell.getValue();
              const cellText = String(cellValue);

              const accountPlatform = selectedAccount?.child_accounts?.find(
                (a) => a.name.trim() === cellText.trim(),
              )?.platform as unknown as Platform;

              // Also get the rendered cell component
              const cellComponent = flexRender(
                cell.column.columnDef.cell,
                cell.getContext(),
              );

              return (
                <div className="flex gap-2 items-center px-1 min-h-[43px]">
                  {accountPlatform && (
                    <img
                      className="h-[14px] w-[14px] flex-shrink-0 -translate-y-[1px]"
                      src={getPlatformIcon(accountPlatform)}
                    />
                  )}

                  <div className="text-[12px] font-medium text-brand-primary-new/80 group-hover/row:text-brand-primary-new">
                    {cellComponent}
                  </div>
                </div>
              );
            }}
          />
        </div>
      )}
    </OverviewSectionWrapper>
  );
};

export default AdAccountOverview;
